import React, { useCallback, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import type { FileUpload, UploadedFile } from '@/components/common/ImageUploader';
import ImageUploader from '@/components/common/ImageUploader';

interface AddAttachmentsDialogProps {
  open: boolean;
  onClose: () => void;
}

const AddAttachmentsDialog: React.FC<AddAttachmentsDialogProps> = ({ open, onClose }) => {
  const { community } = useUser();
  const { requestId } = useParams();
  const [fileUploads, setFileUploads] = useState<FileUpload[]>([]);
  const queryClient = useQueryClient();

  const createRequestMutation = useMutation({
    mutationFn: (files: UploadedFile[]) => {
      return axios.put(`/api/v2/communities/${community?.id}/requests/${requestId}`, {
        files,
      });
    },
  });

  // Handle upload completion from ImageUploader
  const handleUploadComplete = useCallback((fileUploads: FileUpload[]) => {
    setFileUploads(fileUploads);
  }, []);

  const handleUploadFiles = async () => {
    try {
      const successfulUploads = fileUploads
        .filter((upload) => upload.status === 'success' && upload.uploadedFile)
        .map((upload) => upload.uploadedFile!);

      if (successfulUploads.length === 0) {
        toast.error('No files to upload. Please wait for uploads to complete.');
        return;
      }

      createRequestMutation.mutate(successfulUploads, {
        onSuccess: (response) => {
          if (response.status !== 200) {
            toast.error('Failed to upload files');
          } else {
            toast.success('Files uploaded successfully');
            queryClient.invalidateQueries({ queryKey: ['request', requestId] });
            handleClose();
          }
        },
        onError: (error) => {
          toast.error('An error occurred during file upload');
          console.error(error);
        },
      });
    } catch (error) {
      toast.error('An error occurred during file upload');
      console.error(error);
    }
  };

  const handleClose = () => {
    setFileUploads([]);
    onClose();
  };

  // Check if any uploads are in progress
  const uploadsInProgress = fileUploads.some((upload) => upload.status === 'uploading');

  return (
    <Dialog fullWidth maxWidth="md" onClose={handleClose} open={open}>
      <DialogTitle>Add Attachments</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <ImageUploader
            maxFiles={10}
            onUploadComplete={handleUploadComplete}
            subtitle="Drag & drop files here, or click to browse. Upload images, documents, or other files related to this request."
            title="Upload Attachments"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          disabled={createRequestMutation.isPending || uploadsInProgress}
          loading={createRequestMutation.isPending || uploadsInProgress}
          onClick={handleUploadFiles}
          type="submit"
          variant="contained"
        >
          Upload Files
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddAttachmentsDialog;
